import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

type LoanRequestStatus = 'new' | 'review' | 'approved' | 'rejected' | 'contract_sent' | 'active' | 'completed' | 'overdue';
type LoanRequest = { id: string; status: LoanRequestStatus; amount_requested: number; applicant_id: string; user_id: string };

const statusLabels: Record<LoanRequestStatus, string> = {
  new: 'Nová žádost',
  review: 'Ke scoringu',
  approved: 'Schváleno',
  rejected: 'Zamítnuto',
  contract_sent: 'Smlouva odeslána',
  active: 'Aktivní',
  completed: 'Dokončeno',
  overdue: 'Po splatnosti',
};

const statusIcons: Record<LoanRequestStatus, string> = {
  new: '📥',
  review: '📊',
  approved: '✅',
  rejected: '❌',
  contract_sent: '📄',
  active: '🚀',
  completed: '🎉',
  overdue: '⚠️',
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(amount);

export function useRealtimeNotifications() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const loanChannel = supabase
      .channel('loan-requests-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'loan_requests' },
        async (payload: RealtimePostgresChangesPayload<LoanRequest>) => {
          queryClient.invalidateQueries({ queryKey: ['process-counts'] });
          queryClient.invalidateQueries({ queryKey: ['intake-requests'] });
          queryClient.invalidateQueries({ queryKey: ['scoring-requests'] });
          queryClient.invalidateQueries({ queryKey: ['approval-requests'] });
          queryClient.invalidateQueries({ queryKey: ['contracts-approved'] });
          queryClient.invalidateQueries({ queryKey: ['contracts-sent'] });

          const newRecord = payload.new as LoanRequest | null;
          const oldRecord = payload.old as Partial<LoanRequest> | null;

          if (payload.eventType === 'UPDATE' && oldRecord && newRecord) {
            const oldStatus = oldRecord.status;
            const newStatus = newRecord.status;

            if (oldStatus !== newStatus && newStatus) {
              const { data: loanRequest } = await (supabase as any)
                .from('loan_requests')
                .select('applicants(name)')
                .eq('id', newRecord.id)
                .single();

              const applicantName = (loanRequest?.applicants as { name: string } | null)?.name || 'Žádost';
              const icon = statusIcons[newStatus];
              const statusLabel = statusLabels[newStatus];

              toast.info(`${icon} ${applicantName}`, {
                description: `Status změněn na: ${statusLabel}`,
                duration: 5000,
              });
            }
          }

          if (payload.eventType === 'INSERT' && newRecord) {
            const { data: loanRequest } = await (supabase as any)
              .from('loan_requests')
              .select('applicants(name), amount_requested')
              .eq('id', newRecord.id)
              .single();

            const applicantName = (loanRequest?.applicants as { name: string } | null)?.name || 'Neznámý';
            const amount = formatCurrency((loanRequest as any)?.amount_requested || 0);

            toast.info('📥 Nová žádost o zápůjčku', {
              description: `${applicantName} • ${amount}`,
              duration: 5000,
            });
          }
        }
      )
      .subscribe();

    const guestChannel = supabase
      .channel('guest-applications-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'guest_applications' },
        async (payload) => {
          queryClient.invalidateQueries({ queryKey: ['process-counts'] });
          queryClient.invalidateQueries({ queryKey: ['guest-applications'] });
          queryClient.invalidateQueries({ queryKey: ['notifications-pending'] });
          queryClient.invalidateQueries({ queryKey: ['intake-requests'] });

          if (payload.eventType === 'INSERT') {
            const newApp = payload.new as { full_name: string; amount_requested: number };
            toast.info('📥 Nová žádost od neregistrovaného klienta', {
              description: `${newApp.full_name} • ${formatCurrency(newApp.amount_requested)}`,
              duration: 5000,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(loanChannel);
      supabase.removeChannel(guestChannel);
    };
  }, [queryClient]);
}
